import { createRoute, type ParsedLocation, redirect } from '@tanstack/react-router';
import { locationSuffix } from '../lib/guards';
import { useEnvStore } from '../lib/session';
import { authedRoute } from './layouts';

/**
 * Every site-prefixed page is also reachable without its `/$siteId/$siteEnv` (or `/$siteId`)
 * prefix: `/pricing/lists` resolves the user's own site and forwards to the real url, keeping the
 * search and hash - deep links in emails, Intercom and the marketing site are written without a
 * site prefix and must keep working.
 *
 * Each entry below mirrors one route in the families under `siteRoute` / `authedChromeRoute`; the
 * paths are spelled out rather than derived so the route tree keeps its literal types (a computed
 * `path` collapses `to:` back to `string` everywhere in the app). Add the shortcut alongside any
 * new site route.
 */

/**
 * The site to forward a prefix-less url to. The session guards have already resolved it - whichever
 * site was loaded, else the user's own team - so this only reads the answer.
 */
function resolvedSite(): { siteId: string; siteEnv: string } | null {
  const env = useEnvStore.getState().env;
  return env ? { siteId: env.team.slugged_name, siteEnv: env.environment } : null;
}

/** `/pricing/lists` -> `/$siteId/$siteEnv/pricing/lists`. */
function forwardUnderSiteEnv({ location }: { location: ParsedLocation }) {
  const site = resolvedSite();
  if (!site) return;
  throw redirect({
    href: `/${site.siteId}/${site.siteEnv}${location.pathname}${locationSuffix(location)}`,
  });
}

/** `/settings/account` -> `/$siteId/settings/account`. */
function forwardUnderSite({ location }: { location: ParsedLocation }) {
  const site = resolvedSite();
  if (!site) return;
  throw redirect({ href: `/${site.siteId}${location.pathname}${locationSuffix(location)}` });
}

function envShortcut<TPath extends string>(path: TPath) {
  return createRoute({ getParentRoute: () => authedRoute, path, beforeLoad: forwardUnderSiteEnv });
}

function siteShortcut<TPath extends string>(path: TPath) {
  return createRoute({ getParentRoute: () => authedRoute, path, beforeLoad: forwardUnderSite });
}

/** Shortcuts for the `$siteId/$siteEnv` families. */
export const envShortcutLeaves = [
  // site
  envShortcut('/activity'),
  envShortcut('/features'),
  envShortcut('/sales-agents'),
  envShortcut('/orders'),
  envShortcut('/discounts'),
  // forms
  envShortcut('/forms'),
  envShortcut('/forms/$'),
  // configuration
  envShortcut('/configuration'),
  envShortcut('/configuration/onboarding'),
  envShortcut('/configuration/general'),
  envShortcut('/configuration/general/quote-statuses'),
  envShortcut('/configuration/general/notifications'),
  envShortcut('/configuration/general/invoices'),
  envShortcut('/configuration/general/store-details'),
  envShortcut('/configuration/settings'),
  envShortcut('/configuration/settings/general'),
  envShortcut('/configuration/api'),
  envShortcut('/configuration/shipping'),
  envShortcut('/configuration/shipping/create'),
  envShortcut('/configuration/shipping/edit/$id'),
  envShortcut('/configuration/theme'),
  envShortcut('/configuration/theme/editor/$themeId/{-$slug}'),
  envShortcut('/configuration/integrations'),
  envShortcut('/configuration/integrations/platform'),
  envShortcut('/configuration/integrations/oauth-brightpearl'),
  envShortcut('/configuration/integrations/oauth-katana'),
  envShortcut('/configuration/integrations/oauth-quickbooks'),
  envShortcut('/configuration/integrations/oauth-xero'),
  // storefront
  envShortcut('/storefront'),
  envShortcut('/storefront/options'),
  envShortcut('/storefront/widgets'),
  envShortcut('/storefront/widgets/core'),
  envShortcut('/storefront/widgets/pdp'),
  envShortcut('/storefront/widgets/css'),
  envShortcut('/storefront/widgets/tips'),
  // pricing
  envShortcut('/pricing'),
  envShortcut('/pricing/lists'),
  envShortcut('/pricing/lists/create'),
  envShortcut('/pricing/lists/edit/$slug'),
  envShortcut('/pricing/editor'),
  envShortcut('/pricing/editor/$variantId'),
  envShortcut('/pricing/discounts'),
  envShortcut('/pricing/discounts/create'),
  envShortcut('/pricing/discounts/edit/$id'),
  // customers (v1)
  envShortcut('/customers'),
  envShortcut('/customers/groups'),
  envShortcut('/customers/groups/create'),
  envShortcut('/customers/groups/edit/$slug'),
  // customers (v2)
  envShortcut('/v2/customers'),
  envShortcut('/v2/customers/groups'),
  envShortcut('/v2/customers/groups/create'),
  envShortcut('/v2/customers/groups/quick-setup'),
  envShortcut('/v2/customers/groups/edit/$slug'),
  envShortcut('/v2/customers/list'),
  envShortcut('/v2/customers/list/details/$id'),
  envShortcut('/v2/customers/companies'),
  envShortcut('/v2/customers/companies/create'),
  envShortcut('/v2/customers/companies/edit/$id'),
  envShortcut('/v2/customers/companies/$id/sections'),
  envShortcut('/v2/customers/companies/$id/sections/create'),
  envShortcut('/v2/customers/companies/$id/sections/edit/$sectionId'),
  envShortcut('/v2/customers/agents'),
  envShortcut('/v2/customers/agents/details/$id'),
  envShortcut('/v2/customers/roles'),
  envShortcut('/v2/customers/roles/create'),
  envShortcut('/v2/customers/roles/edit/$slug'),
  // integrations
  envShortcut('/integrations'),
  envShortcut('/integrations/platform'),
  envShortcut('/integrations/platform/metafields'),
  envShortcut('/integrations/partners'),
  envShortcut('/integrations/partners/oauth/brightpearl'),
  envShortcut('/integrations/partners/oauth/katana'),
  envShortcut('/integrations/partners/oauth/quickbooks'),
  envShortcut('/integrations/partners/oauth/xero'),
  envShortcut('/integrations/customers-sync'),
  envShortcut('/integrations/customers-sync/$identifier'),
  envShortcut('/integrations/products-sync'),
  envShortcut('/integrations/products-sync/$variantSku'),
  envShortcut('/integrations/widgets'),
  envShortcut('/integrations/widgets/core'),
  envShortcut('/integrations/widgets/pdp'),
  envShortcut('/integrations/widgets/css'),
  envShortcut('/integrations/widgets/tips'),
  // analytics
  envShortcut('/analytics'),
  envShortcut('/analytics/dashboard'),
  envShortcut('/analytics/chat'),
  envShortcut('/analytics/report/$reportSlug'),
  envShortcut('/analytics/custom/$customId'),
];

/** Shortcuts for the families that carry a `$siteId` but no `$siteEnv`. */
export const siteShortcutLeaves = [
  // partners (`/overview` is the legacy url, redirected on arrival)
  siteShortcut('/partners'),
  siteShortcut('/partners/overview'),
  // settings
  siteShortcut('/settings'),
  siteShortcut('/settings/account'),
  siteShortcut('/settings/password'),
  siteShortcut('/settings/plan'),
  siteShortcut('/settings/team'),
  siteShortcut('/settings/team/invite'),
  // staff
  siteShortcut('/staff'),
  siteShortcut('/staff/manage-billing'),
  siteShortcut('/staff/manage-billing/$actionType'),
  siteShortcut('/staff/manage-team'),
  siteShortcut('/staff/manage-team/$actionType'),
  siteShortcut('/staff/customer-sync'),
];
